import type { ReactNode } from 'react';
import type { AllocationResult } from '../../lib/allocation';
import { formatCentsAsDollars } from '../../lib/money';
import type { ReceiptLineItem } from '../../lib/receiptClient';
import { PERSONAL, SPLIT_CHOICE, type ItemAllocation, type ItemDraft } from '../../lib/receiptItems';
import { BlockReasons, ChipGroup, DollarInput, NavRow, type SpendLine } from './controls';
import styles from '../AddSpending.module.css';

// "Split it": the lines, the "Not for the properties" bucket, the sales tax (amounts mode) and
// the always-visible "Remaining to assign". Next is the gate — disabled while anything blocks,
// with every reason shown in plain words. When the receipt's items pass the Build C gate, a third
// mode taps each item onto a line; the extracted tax is then shown, not typed.

export type SplitMode = 'items' | 'amounts' | 'percentages';

// Cents with a leading minus for a discount or a negative line, e.g. "−$1.00".
function signedDollars(cents: number): string {
  return `${cents < 0 ? '−' : ''}$${formatCentsAsDollars(Math.abs(cents))}`;
}

export interface AssignScreenProps {
  banner: ReactNode;
  totalCents: number;
  splitMode: SplitMode;
  onSplitMode: (mode: SplitMode) => void;
  // Build C. itemsOffered = the gate is open and the typed total is the extracted total; when
  // false the screen is exactly the two-mode screen.
  itemsOffered: boolean;
  items: ReceiptLineItem[];
  itemDrafts: ItemDraft[];
  onItemDraft: (index: number, draft: ItemDraft) => void;
  itemAllocation: ItemAllocation | null;
  taxCents: number;
  fiftyFiftyLabel: string | null; // null = the preset is not offered
  onFiftyFifty: () => void;
  lines: SpendLine[];
  onLineChange: (key: string, change: (l: SpendLine) => SpendLine) => void;
  onLineDetails: (key: string) => void;
  onLineRemove: (key: string) => void;
  onAddLine: () => void;
  labelOf: (propertyId: string) => string;
  categoryLabel: (key: string) => string;
  personalText: string;
  onPersonalText: (text: string) => void;
  personalPercentText: string;
  onPersonalPercentText: (text: string) => void;
  salesTaxText: string;
  onSalesTaxText: (text: string) => void;
  salesTaxEntered: boolean;
  allocation: AllocationResult;
  blockReasons: string[];
  onBack: () => void;
  onNext: () => void;
}

export default function AssignScreen(props: AssignScreenProps) {
  const { allocation, lines } = props;
  const percentages = props.splitMode === 'percentages';
  const amounts = props.splitMode === 'amounts';
  const itemsMode = props.splitMode === 'items';
  const remaining = allocation.remaining;
  const done = remaining.cents === 0 && (remaining.percent ?? 0) === 0;
  const writtenOf = (key: string) =>
    allocation.lines.find((l) => l.id === key)?.writtenCents ?? 0;
  const lineTargetLabel = (line: SpendLine, i: number) =>
    line.propertyId ? `Line ${i + 1} · ${props.labelOf(line.propertyId)}` : `Line ${i + 1}`;

  return (
    <main className={styles.container}>
      {props.banner}
      <h2 className={styles.question}>Split ${formatCentsAsDollars(props.totalCents)}</h2>
      <div className={styles.modeRow} role="group" aria-label="How to split">
        {props.itemsOffered && (
          <button
            type="button"
            className={itemsMode ? styles.chipSelected : styles.chip}
            aria-pressed={itemsMode}
            onClick={() => props.onSplitMode('items')}
          >
            Tap items
          </button>
        )}
        <button
          type="button"
          className={amounts ? styles.chipSelected : styles.chip}
          aria-pressed={amounts}
          onClick={() => props.onSplitMode('amounts')}
        >
          Type amounts
        </button>
        <button
          type="button"
          className={percentages ? styles.chipSelected : styles.chip}
          aria-pressed={percentages}
          onClick={() => props.onSplitMode('percentages')}
        >
          Use percentages
        </button>
      </div>
      {props.fiftyFiftyLabel && !itemsMode && (
        <button type="button" className={styles.secondaryButton} onClick={props.onFiftyFifty}>
          {props.fiftyFiftyLabel}
        </button>
      )}
      {props.salesTaxEntered && !itemsMode && (
        <p className={styles.overrideNote}>
          Amounts are before tax. The tax is shared out in proportion.
        </p>
      )}
      {itemsMode && props.taxCents > 0 && (
        <p className={styles.overrideNote}>
          Item amounts are before tax. The tax is shared out in proportion.
        </p>
      )}

      {itemsMode && (
        <>
          <label className={styles.fieldLabel}>Receipt items</label>
          <div className={styles.lineCardList}>
            {props.items.map((item, i) => {
              const draft = props.itemDrafts[i] ?? { choice: '', splitText: {} };
              const onDraft = (next: ItemDraft) => props.onItemDraft(i, next);
              return (
                <div key={i} className={styles.lineCard}>
                  <span className={`${styles.lineCardTitle} ${styles.itemText}`}>
                    {item.description.trim() || `Item ${i + 1}`}
                  </span>
                  <span className={styles.lineCardDetail}>
                    {item.amountCents < 0 && <span className={styles.discountTag}>Discount</span>}
                    {signedDollars(item.amountCents)}
                  </span>
                  <div className={styles.itemChoices}>
                    <ChipGroup
                      ariaLabel={`Where item ${i + 1} goes`}
                      options={[
                        ...lines.map((line, li) => ({ value: line.id, label: lineTargetLabel(line, li) })),
                        { value: PERSONAL, label: 'Not for the properties' },
                        { value: SPLIT_CHOICE, label: 'Split this item' },
                      ]}
                      selected={draft.choice}
                      onSelect={(choice) => onDraft({ ...draft, choice })}
                    />
                  </div>
                  {draft.choice === SPLIT_CHOICE && (
                    <>
                      <label className={styles.fieldLabel}>Split item {i + 1} by percent</label>
                      {[
                        ...lines.map((line, li) => ({
                          target: line.id,
                          label: lineTargetLabel(line, li),
                          ariaLabel: `Item ${i + 1} percent for line ${li + 1}`,
                        })),
                        {
                          target: PERSONAL,
                          label: 'Not for the properties',
                          ariaLabel: `Item ${i + 1} percent not for the properties`,
                        },
                      ].map((t) => (
                        <div key={t.target} className={styles.inputRow}>
                          <input
                            className={styles.amountInput}
                            type="text"
                            inputMode="numeric"
                            placeholder="0"
                            value={draft.splitText[t.target] ?? ''}
                            onChange={(e) =>
                              onDraft({ ...draft, splitText: { ...draft.splitText, [t.target]: e.target.value } })
                            }
                            aria-label={t.ariaLabel}
                          />
                          <span className={styles.inputSuffix}>% {t.label}</span>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <div className={styles.lineCardList}>
        {lines.map((line, i) => (
          <div key={line.id} className={styles.lineCard}>
            <span className={styles.lineCardTitle}>
              {line.propertyId ? props.labelOf(line.propertyId) : `Line ${i + 1}: choose a property`}
            </span>
            <span className={styles.lineCardDetail}>
              {line.taxCategoryKey ? props.categoryLabel(line.taxCategoryKey) : 'No category yet'}
            </span>
            {itemsMode ? (
              <>
                <span className={styles.lineCardDetail}>
                  Items before tax: {signedDollars(props.itemAllocation?.lineCents[line.id] ?? 0)}
                </span>
                <span className={styles.lineCardDetail}>= {signedDollars(writtenOf(line.id))}</span>
              </>
            ) : (
              <div className={styles.inputRow}>
                {percentages ? (
                  <>
                    <input
                      className={styles.amountInput}
                      type="text"
                      inputMode="numeric"
                      placeholder="0"
                      value={line.percentText}
                      onChange={(e) =>
                        props.onLineChange(line.id, (l) => ({ ...l, percentText: e.target.value }))
                      }
                      aria-label={`Line ${i + 1} percent`}
                    />
                    <span className={styles.inputSuffix}>%</span>
                  </>
                ) : (
                  <DollarInput
                    text={line.amountText}
                    onText={(t) => props.onLineChange(line.id, (l) => ({ ...l, amountText: t }))}
                    ariaLabel={`Line ${i + 1} amount in dollars`}
                  />
                )}
              </div>
            )}
            {percentages && (
              <span className={styles.lineCardDetail}>
                = ${formatCentsAsDollars(writtenOf(line.id))}
              </span>
            )}
            <div className={styles.lineCardActions}>
              <button className={styles.smallButton} onClick={() => props.onLineDetails(line.id)}>
                Details
              </button>
              <button className={styles.removeButton} onClick={() => props.onLineRemove(line.id)}>
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>
      <button className={styles.secondaryButton} onClick={props.onAddLine}>
        + Add a line
      </button>

      {itemsMode ? (
        <div className={styles.summaryList}>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Not for the properties</span>
            <span className={styles.summaryValue}>= {signedDollars(allocation.personal.writtenCents)}</span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Sales tax on the receipt</span>
            <span className={styles.summaryValue}>${formatCentsAsDollars(props.taxCents)}</span>
          </div>
        </div>
      ) : (
        <>
          <label className={styles.fieldLabel}>Not for the properties (optional)</label>
          <div className={styles.inputRow}>
            {percentages ? (
              <>
                <input
                  className={styles.amountInput}
                  type="text"
                  inputMode="numeric"
                  placeholder="0"
                  value={props.personalPercentText}
                  onChange={(e) => props.onPersonalPercentText(e.target.value)}
                  aria-label="Not for the properties percent"
                />
                <span className={styles.inputSuffix}>%</span>
              </>
            ) : (
              <DollarInput
                text={props.personalText}
                onText={props.onPersonalText}
                ariaLabel="Not for the properties in dollars"
              />
            )}
          </div>
        </>
      )}

      {amounts && (
        <>
          <label className={styles.fieldLabel}>Sales tax (optional)</label>
          <DollarInput
            text={props.salesTaxText}
            onText={props.onSalesTaxText}
            ariaLabel="Sales tax in dollars"
          />
        </>
      )}

      <div
        className={`${styles.remaining} ${done ? styles.remainingDone : ''}`}
        role="status"
        aria-label="Remaining to assign"
      >
        <span>Remaining to assign</span>
        <span>
          {remaining.cents < 0 ? '−' : ''}${formatCentsAsDollars(Math.abs(remaining.cents))}
          {remaining.percent !== null ? ` (${remaining.percent}%)` : ''}
        </span>
      </div>
      <BlockReasons reasons={props.blockReasons} />
      <NavRow onBack={props.onBack} onNext={props.onNext} canNext={props.blockReasons.length === 0} />
    </main>
  );
}
